import { connect, type ConnectedProps } from "react-redux";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { usePerformanceFilter } from "features/Library/Common/Hooks/usePerformanceFilter";
import { SummarySection } from "./SummarySection";
import { CustomersSection } from "./CustomersSection";
import MostActiveCustomersSection from "./MostActiveCustomersSection";
import ReportingFilter from "components/reportingFilter/ReportingFilter";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { TemplateActivityChart } from "./TemplateActivityChart";
import { TemplateEnrollmentChart } from "./TemplateEnrollmentChart";
import styles from "./templatePerformance.module.scss";
import "features/Library/Common/utils/performanceSCSSUtils.scss";
import { useEffect, type FC } from "react";
import { ContentView } from "components";
import TemplateFlowEngagement from "./TemplateFlowEngagement";
import TemplateEmailEngagement from "./TemplateEmailEngagement";
import { TemplatePrioritizedFlows } from "./TemplatePrioritizedFlows";
import { setExportAction } from "features/Reporting/state/export/exportSlice";
import { bindAction } from "interfaces";
import { getTemplateExport } from "../../state/actions/templateActions";
import ReportingExport from "components/reportingExport/ReportingExport";

const ignoreInCountAndHide = ["type"];

export interface Props extends PropsFromRedux {
  templateId: number;
  templateName: string;
}

export const TemplatePerformance: FC<Props> = ({
  accountId,
  templateId,
  templateName,
  getTemplateExport,
  setExport,
}) => {
  const isTemplatePerformanceEnabled = useFeatureFlag(FeatureFlags.TemplatePerformance);
  const [filter, setFilter] = usePerformanceFilter({
    includeAccountsDropdown: false,
    includePriority: true,
    accountId,
  });

  // Setting appropriate export method
  useEffect(() => {
    setExport({
      // @ts-ignore
      method: getTemplateExport,
      args: [filter, templateId, templateName],
    });
  }, [filter, setExport, templateId, getTemplateExport, templateName]);

  if (!isTemplatePerformanceEnabled) {
    return <ReportUnavailable />;
  }

  const contentViewTabs = [
    {
      id: "templatePrioritizedFlows",
      label: "Prioritized Flows",
      component: <TemplatePrioritizedFlows templateId={templateId} filter={filter} />,
    },
    {
      id: "templateFlowEngagement",
      label: "Flow Engagement",
      component: <TemplateFlowEngagement templateId={templateId} filter={filter} />,
    },
    {
      id: "templateEmailEngagement",
      label: "Email Engagement",
      component: <TemplateEmailEngagement templateId={templateId} filter={filter} />,
    },
  ];

  return (
    <div className={`performanceRoot performanceBody ${styles.templatePerformanceBody}`}>
      <div className={`performanceHeader ${styles.performanceHeader}`}>
        <h2 className={`${styles.summaryHeader}`}>Summary</h2>
        <div className="performanceActions">
          <ReportingFilter
            currentFilter={filter}
            callback={setFilter}
            ignoreInCountAndHide={ignoreInCountAndHide}
            includePriority
          />
          <ReportingExport />
        </div>
      </div>
      <SummarySection templateId={templateId} filter={filter} />
      <TemplateActivityChart templateId={templateId} filter={filter} />
      <TemplateEnrollmentChart templateId={templateId} filter={filter} />
      <CustomersSection templateId={templateId} filter={filter} />
      <MostActiveCustomersSection templateId={templateId} filter={filter} />
      <div className={styles.summaryHeader}>
        <h2 className={styles.summaryHeader}>Content</h2>
      </div>
      <div className={styles.bottomReport}>
        <ContentView tabs={contentViewTabs} />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getTemplateExport: bindAction(getTemplateExport, dispatch),
  setExport: bindAction(setExportAction, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
const ConnectedComponent = connector(TemplatePerformance);
export default ConnectedComponent;
